/**
 * Application Configuration
 *
 * Sets up providers for the support app including:
 * - Zoneless change detection (modern Angular approach)
 * - Routing
 * - Animations
 * - HTTP client
 * - Firebase services
 * - Theme initialization
 */

import {
  ApplicationConfig,
  APP_INITIALIZER,
  ErrorHandler,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient } from '@angular/common/http';
import { provideFirebaseApp, initializeApp, getApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
import { provideDatabase, getDatabase } from '@angular/fire/database';
import {
  connectFunctionsEmulator,
  getFunctions,
  provideFunctions,
} from '@angular/fire/functions';
import { log } from '@app/_services/logger';
import {
  initializeAppCheck,
  provideAppCheck,
  ReCaptchaEnterpriseProvider,
} from '@angular/fire/app-check';
import { MAT_DATE_LOCALE } from '@angular/material/core';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { ThemeService } from './_services/theme.service';
import { CustomErrorHandler } from './_services/custom-error-handler';

export const appConfig: ApplicationConfig = {
  providers: [
    // Global error handler
    { provide: ErrorHandler, useClass: CustomErrorHandler },

    // Zoneless change detection (modern Angular approach with signals)
    provideZonelessChangeDetection(),

    // Router + animations
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(),

    // Firebase
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),

    // Realtime Database (support RTDB instance)
    provideDatabase(() => getDatabase(getApp(), environment.firebase.supportDatabaseURL)),

    // Functions with emulator support
    provideFunctions(() => {
      // Use official API to set region to europe-west1
      const functions = getFunctions(getApp(), 'europe-west1');
      if (environment.useLocalFunctions) {
        connectFunctionsEmulator(
          functions,
          environment.localFunctionsHost,
          environment.localFunctionsPort
        );
        log.info('AppConfig', 'Connected to functions emulator');
      }
      return functions;
    }),

    // App Check (prod/hotfix only)
    ...((environment.env === 'prod' || environment.env === 'hotfix')
      ? [
          provideAppCheck(() => {
            return initializeAppCheck(getApp(), {
              provider: new ReCaptchaEnterpriseProvider(
                environment.recaptchaSiteKey
              ),
              isTokenAutoRefreshEnabled: true,
            });
          }),
        ]
      : []),

    // Material date locale
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },

    // Theme initialization (runs on app start)
    {
      provide: APP_INITIALIZER,
      useFactory: (themeService: ThemeService) => () => {
        // Theme is applied in constructor, this just ensures DI creates it early
      },
      deps: [ThemeService],
      multi: true,
    },
  ],
};
